/**
 * SessionStorage persistence for recovery state
 * Stores minimal, non-sensitive state with versioning
 */

import { RecoveryStep } from './types';
import logger from '../../utils/logger';
import * as credentialStore from '../../services/credentialStore';

// Constants
export const STORAGE_KEY = 'recovery:v1';
export const RECOVERY_COMPLETION_KEY = 'recovery:completed';
export const RECOVERY_SKIPPED_KEY = 'recovery:skipped';
export const VERSION = 1;

// Old keys to clean up during migration
const OLD_KEYS = ['recovery:v0', 'recovery_state', 'matrix_recovery'];

// Throttle configuration
let writeTimer = null;
const WRITE_THROTTLE_MS = 250;

/**
 * Check if a step should be persisted
 * Transient steps are normalized to avoid resuming mid-operation
 * @param {string} step - Current step
 * @returns {boolean}
 */
export function shouldPersist(step) {
  // Don't persist transient operational steps
  const transientSteps = [RecoveryStep.VALIDATING, RecoveryStep.RESTORING];
  return !transientSteps.includes(step);
}

/**
 * Normalize step for persistence
 * Transient steps are stored as AWAIT_KEY to avoid resuming mid-operation
 * @param {string} step - Current step
 * @returns {string}
 */
function normalizeStep(step) {
  const transientSteps = [RecoveryStep.VALIDATING, RecoveryStep.RESTORING];
  if (transientSteps.includes(step)) {
    return RecoveryStep.AWAIT_KEY;
  }
  return step;
}

/**
 * Save state to sessionStorage
 * Only stores non-sensitive fields
 * @param {Object} state - Recovery state to save
 */
export function saveState(state) {
  try {
    // Clear any pending write timer
    if (writeTimer) {
      clearTimeout(writeTimer);
    }

    // Throttle writes to avoid excessive I/O
    writeTimer = setTimeout(() => {
      try {
        // Only persist if not in transient state
        if (!shouldPersist(state.step)) {
          logger.debug('RECOVERY: [Storage] Skipping save for transient step:', state.step);
          return;
        }

        // Extract only non-sensitive fields
        const toStore = {
          version: VERSION,
          hasBackup: state.hasBackup,
          needsRecovery: state.needsRecovery,
          step: normalizeStep(state.step),
          progress: {
            imported: state.progress?.imported || 0,
            total: state.progress?.total || 0,
            percent: state.progress?.percent || 0,
          },
          startedAt: state.startedAt,
        };

        // Never store error details or keys
        // Error codes could leak sensitive information

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
        logger.debug('RECOVERY: [Storage] State saved:', {
          step: toStore.step,
          needsRecovery: toStore.needsRecovery,
          hasBackup: toStore.hasBackup,
        });
      } catch (error) {
        logger.error('[RecoveryStorage] Failed to save state:', error);
      }
    }, WRITE_THROTTLE_MS);
  } catch (error) {
    logger.error('[RecoveryStorage] Failed to schedule save:', error);
  }
}

/**
 * Load state from sessionStorage
 * @returns {Object|null} Loaded state or null if not found/invalid
 */
export function loadState() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      logger.debug('RECOVERY: [Storage] No stored state found');
      return null;
    }

    const parsed = JSON.parse(stored);

    // Check version
    if (parsed.version !== VERSION) {
      logger.warn('[RecoveryStorage] Version mismatch, clearing old state');
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    logger.debug('RECOVERY: [Storage] State loaded:', {
      step: parsed.step,
      needsRecovery: parsed.needsRecovery,
      hasBackup: parsed.hasBackup,
    });
    return {
      hasBackup: Boolean(parsed.hasBackup),
      needsRecovery: Boolean(parsed.needsRecovery),
      step: parsed.step || RecoveryStep.IDLE,
      progress: parsed.progress || { imported: 0, total: 0, percent: 0 },
      startedAt: parsed.startedAt || null,
    };
  } catch (error) {
    logger.error('[RecoveryStorage] Failed to load state:', error);
    // Clear corrupted data
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Clear stored state
 */
export function clearState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    logger.debug('RECOVERY: [Storage] State cleared');
  } catch (error) {
    logger.error('[RecoveryStorage] Failed to clear state:', error);
  }
}

/**
 * Migrate from old storage keys
 * Clears any legacy recovery state
 */
export function migrateOldKeys() {
  try {
    OLD_KEYS.forEach(key => {
      if (sessionStorage.getItem(key)) {
        logger.info('[RecoveryStorage] Removing old key:', key);
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    logger.error('[RecoveryStorage] Failed to migrate old keys:', error);
  }
}

/**
 * Get device ID from stored Matrix credentials
 * @returns {string|null} Device ID or null if not available
 */
function getDeviceIdFromCredentials() {
  const creds = credentialStore.readCredentials();
  return creds?.deviceId || null;
}

/**
 * Hydrate initial state with stored state
 * Merges stored state with defaults
 * @param {Object} initialState - Default initial state
 * @returns {Object} Hydrated state
 */
export function hydrate(initialState) {
  try {
    // Clean up old keys first
    migrateOldKeys();

    // Check if recovery was completed for this device BEFORE loading sessionStorage
    // This prevents stale sessionStorage from showing the modal incorrectly
    const deviceId = getDeviceIdFromCredentials();
    if (deviceId && isRecoveryCompleted(deviceId)) {
      logger.debug('RECOVERY: [Storage] Recovery completed for device, clearing any stale sessionStorage');
      clearState();
      return {
        ...initialState,
        needsRecovery: false,
        step: RecoveryStep.DONE,
      };
    }

    // Load stored state
    const stored = loadState();
    if (!stored) {
      logger.debug('RECOVERY: [Storage] No stored state, using initial state');
      return initialState;
    }

    // If the stored state shows recovery is DONE, preserve it
    // This prevents the modal from reappearing after successful recovery
    if (stored.step === RecoveryStep.DONE) {
      logger.debug('RECOVERY: [Storage] Previous recovery completed, preserving state with needsRecovery=false');
      // Return the stored state with needsRecovery explicitly false
      // This ensures the modal doesn't reappear on refresh
      return {
        ...initialState,
        ...stored,
        needsRecovery: false, // Ensure this is false after successful recovery
        step: RecoveryStep.DONE,
      };
    }

    // Merge with initial state
    const hydrated = {
      ...initialState,
      ...stored,
      // Never restore error state or transient data
      error: null,
    };

    // Validate the hydrated state
    // If we had recovery in progress but step is done, clear it
    if (hydrated.step === RecoveryStep.DONE && hydrated.needsRecovery) {
      hydrated.needsRecovery = false;
    }

    logger.debug('RECOVERY: [Storage] State hydrated:', {
      step: hydrated.step,
      needsRecovery: hydrated.needsRecovery,
      hasBackup: hydrated.hasBackup,
    });

    return hydrated;
  } catch (error) {
    logger.error('[RecoveryStorage] Failed to hydrate:', error);
    return initialState;
  }
}

/**
 * Cancel any pending write operations
 * Call this when unmounting to avoid memory leaks
 */
export function cancelPendingWrites() {
  if (writeTimer) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
}

/**
 * Mark recovery as completed for this device
 * @param {string} deviceId - The device ID that completed recovery
 */
export function markRecoveryCompleted(deviceId) {
  try {
    const data = {
      deviceId,
      completedAt: Date.now(),
    };
    localStorage.setItem(RECOVERY_COMPLETION_KEY, JSON.stringify(data));
    logger.debug('RECOVERY: [Storage] Marked recovery as completed for device:', deviceId);
  } catch (error) {
    logger.error('RECOVERY: [Storage] Failed to mark recovery as completed:', error);
  }
}

/**
 * Check if recovery was completed for this device
 * @param {string} deviceId - The device ID to check
 * @returns {boolean} True if recovery was completed for this device
 */
export function isRecoveryCompleted(deviceId) {
  try {
    const stored = localStorage.getItem(RECOVERY_COMPLETION_KEY);
    if (!stored) {
      return false;
    }
    const data = JSON.parse(stored);
    const isCompleted = data.deviceId === deviceId;
    logger.debug('RECOVERY: [Storage] Recovery completed check for device:', deviceId, '-> ', isCompleted);
    return isCompleted;
  } catch (error) {
    logger.error('RECOVERY: [Storage] Failed to check recovery completion:', error);
    return false;
  }
}

/**
 * Clear the recovery completion marker
 */
export function clearRecoveryCompletion() {
  try {
    localStorage.removeItem(RECOVERY_COMPLETION_KEY);
    logger.debug('RECOVERY: [Storage] Cleared recovery completion marker');
  } catch (error) {
    logger.error('RECOVERY: [Storage] Failed to clear recovery completion:', error);
  }
}